import { useEffect, useMemo, useRef } from 'react';

import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { RaColorPicker } from '@modules/_shared/components/form/RaColorPicker';
import {
  RaSelect,
  type RaSelectOption,
} from '@modules/_shared/components/form/RaSelect';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { ReportFontSelect } from '@modules/reports/shared/ReportFontSelect';
import { withCurrentTheme } from '@modules/reports/shared/withCurrentTheme';
import { type Translate, useLocale } from '@shared/hooks/locale';

export type DocTemplate = {
  id: string;
  name: string;
};

export type ReportSettingsSectionProps<
  _TFormData extends Record<string, any> = Record<string, any>,
> = {
  expanded: boolean;
  onToggle: () => void;
  docTemplatesData?: DocTemplate[];
  themeTemplatesData?: DocTemplate[];
  currentTemplateId?: string | null;
  currentThemeId?: string | null;
  currentThemeName?: string | null;
  includeLanguage?: boolean;
};

export const getLanguageOptions = (t: Translate): RaSelectOption[] => [
  { value: 'en', label: t('english') },
  { value: 'fr', label: t('french') },
  { value: 'it', label: t('italian') },
  { value: 'pl', label: t('polish') },
  { value: 'pt', label: t('portuguese') },
  { value: 'es', label: t('spanish') },
  { value: 'de', label: t('german') },
];

const useSyncSavedIdToForm = (
  fieldName: string,
  savedId: string | null | undefined,
  syncNull: boolean,
) => {
  const { getValues, setValue } = useFormContext();
  const lastSavedId = useRef(savedId);
  useEffect(() => {
    if (lastSavedId.current === savedId || (savedId == null && !syncNull)) {
      return;
    }
    lastSavedId.current = savedId;
    if (getValues(fieldName) !== savedId) {
      setValue(fieldName, savedId);
    }
  }, [fieldName, savedId, syncNull, getValues, setValue]);
};

export const ReportSettingsSection = ({
  expanded,
  onToggle,
  docTemplatesData,
  themeTemplatesData,
  currentTemplateId,
  currentThemeId,
  currentThemeName,
  includeLanguage = false,
}: ReportSettingsSectionProps) => {
  const { t } = useLocale();
  const { control } = useFormContext();
  const { me } = useAppData();

  useSyncSavedIdToForm('doc_template_theme_id', currentThemeId ?? null, true);
  useSyncSavedIdToForm('doc_template_id', currentTemplateId, false);

  const canEditBranding =
    me?.is_admin || (me?.user_ids_in_led_teams?.length ?? 0) > 0;

  const templateOptions = useMemo<RaSelectOption[]>(
    () =>
      docTemplatesData?.map(template => ({
        value: template.id,
        label: template.name,
      })) || [],
    [docTemplatesData],
  );
  const themeOptions = useMemo<RaSelectOption[]>(
    () =>
      withCurrentTheme(themeTemplatesData, {
        id: currentThemeId,
        name: currentThemeName ?? t('Current theme'),
      }).map(template => ({
        value: template.id,
        label: template.name,
      })),
    [themeTemplatesData, currentThemeId, currentThemeName, t],
  );
  const languageOptions = useMemo(
    () => (includeLanguage ? getLanguageOptions(t) : []),
    [includeLanguage, t],
  );
  const showTemplateField = useMemo(
    () => (docTemplatesData?.length ?? 0) > 1,
    [docTemplatesData],
  );

  return (
    <Box
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
          px: 2,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={onToggle}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <SettingsIcon size={20} color={'var(--mui-palette-text-secondary)'} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('Settings')}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            onToggle();
          }}
        >
          {expanded ? (
            <ChevronUpIcon size={20} />
          ) : (
            <ChevronDownIcon size={20} />
          )}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pt: 1, px: 2, pb: 2 }}>
          <Stack spacing={2}>
            <RaSelect
              name="doc_template_theme_id"
              label={t('Theme')}
              control={control}
              options={themeOptions}
            />
            <ReportFontSelect name="font_family" control={control} />
            {includeLanguage && (
              <RaSelect
                name="language"
                label={t('Language')}
                control={control}
                options={languageOptions}
              />
            )}
            {showTemplateField && (
              <RaSelect
                name="doc_template_id"
                label={t('Template')}
                control={control}
                options={templateOptions}
              />
            )}
            {canEditBranding && (
              <RaColorPicker
                name="branding_color"
                label={t('Branding color')}
                control={control}
              />
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};
